import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import apiService from 'app/apiService';
import EmailTemplateApiService from 'app/apiService/emailTemplate/emailTemplateApiService';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import DataGridTypes from 'types/DataGridTypes';
import type { UseResolveActionsModalMAIQOptions } from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveActionsModalMAIQ/types';

function getErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: unknown }).message);
  }
  return '';
}

const RESOLVE_MODAL_TEMPLATES_PAGE_SIZE = 200;

export function useResolveActionsModalMAIQ(options: UseResolveActionsModalMAIQOptions) {
  const { dealerId, templateIds, segmentName = '', onSuccess } = options;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [resolvedTemplateIds, setResolvedTemplateIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [gridRefreshKey, setGridRefreshKey] = useState(0);
  const [previewTemplateId, setPreviewTemplateId] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const activeTemplateIds = useMemo(
    () => templateIds.filter(id => !resolvedTemplateIds.has(id)),
    [resolvedTemplateIds, templateIds],
  );

  const emailTemplatesGridType = useMemo(() => {
    if (!dealerId.trim() || !activeTemplateIds.length) return null;
    const metadataFilters: Record<string, string[]> = {
      status: ['PENDING_APPROVAL'],
    };
    const segmentTrim = segmentName.trim();
    if (segmentTrim) {
      metadataFilters.segment_name = [segmentTrim];
    }
    const grid = DataGridTypes.HIL_PENDING_APPROVAL_TEMPLATES(
      dealerId,
      undefined,
      undefined,
      metadataFilters,
    );
    grid.pageSize = RESOLVE_MODAL_TEMPLATES_PAGE_SIZE;
    return grid;
  }, [activeTemplateIds.length, dealerId, segmentName]);

  const refreshGrid = useCallback(() => {
    void apiService.invalidateCacheByPrefix(
      EmailTemplateApiService.ENDPOINT,
      'GET',
    );
    queryClient.removeQueries({
      queryKey: ['GET', EmailTemplateApiService.ENDPOINT],
      exact: false,
    });
    setGridRefreshKey(prev => prev + 1);
  }, [queryClient]);

  const markTemplatesResolved = useCallback((ids: string[]) => {
    const normalized = ids.map(id => String(id).trim()).filter(Boolean);
    if (!normalized.length) return;
    setResolvedTemplateIds(prev => {
      const next = new Set(prev);
      for (const id of normalized) {
        next.add(id);
      }
      return next;
    });
  }, []);

  const notifySuccess = useCallback(
    (message: string) => {
      if (!message) return;
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'success',
          message,
        }),
      );
    },
    [dispatch],
  );

  const notifyError = useCallback(
    (message: string) => {
      if (!message) return;
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'error',
          message,
        }),
      );
    },
    [dispatch],
  );

  const runMutation = useCallback(
    async (
      fn: () => Promise<{ message?: string }>,
      affectedTemplateIds: string[] = [],
    ) => {
      setActionLoading(true);
      try {
        const response = await fn();
        const msg = response.message ? String(response.message) : '';
        if (msg) notifySuccess(msg);
        markTemplatesResolved(affectedTemplateIds);
        setSelectedRowIds([]);
        refreshGrid();
        onSuccess?.();
      } catch (err: unknown) {
        const msg = getErrorMessage(err);
        if (msg) notifyError(msg);
      } finally {
        setActionLoading(false);
      }
    },
    [markTemplatesResolved, notifyError, notifySuccess, onSuccess, refreshGrid],
  );

  const handleBulkApprove = useCallback(() => {
    if (!selectedRowIds.length) return;
    const ids = [...selectedRowIds];
    void runMutation(
      () => EmailTemplateApiService.bulkApproveEmailTemplates(dealerId, ids),
      ids,
    );
  }, [dealerId, runMutation, selectedRowIds]);

  const handleBulkReject = useCallback(() => {
    if (!selectedRowIds.length) return;
    const ids = [...selectedRowIds];
    void runMutation(
      () => EmailTemplateApiService.bulkRejectEmailTemplates(dealerId, ids),
      ids,
    );
  }, [dealerId, runMutation, selectedRowIds]);

  const handleApproveRow = useCallback(
    (templateId: string) => {
      const idTrim = templateId?.trim();
      if (!idTrim) return;
      void runMutation(
        () => EmailTemplateApiService.approveEmailTemplate(dealerId, idTrim),
        [idTrim],
      );
    },
    [dealerId, runMutation],
  );

  const handleRejectRow = useCallback(
    (templateId: string) => {
      const idTrim = templateId?.trim();
      if (!idTrim) return;
      void runMutation(
        () => EmailTemplateApiService.rejectEmailTemplate(dealerId, idTrim),
        [idTrim],
      );
    },
    [dealerId, runMutation],
  );

  const handleCellClick = useCallback(
    (cell: { row?: { _id?: string }; field?: string }, clickedElement?: string) => {
      const row = cell?.row;
      if (!row || cell.field !== 'action' || !row._id || !clickedElement) return;

      if (clickedElement === 'preview') {
        setPreviewTemplateId(row._id);
        setPreviewOpen(true);
      } else if (clickedElement === 'approve') {
        handleApproveRow(row._id);
      } else if (clickedElement === 'reject') {
        handleRejectRow(row._id);
      }
    },
    [handleApproveRow, handleRejectRow],
  );

  const handleMultiSelect = useCallback(
    (_selectionModel: string[], selectedRows?: Array<{ _id?: string }>) => {
      const ids = (selectedRows ?? [])
        .map(row => row?._id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
      setSelectedRowIds(ids);
    },
    [],
  );

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewTemplateId('');
  }, []);

  const resetModalState = useCallback(() => {
    setSelectedRowIds([]);
    setResolvedTemplateIds(new Set());
    setPreviewOpen(false);
    setPreviewTemplateId('');
    setGridRefreshKey(0);
  }, []);

  return {
    activeTemplateIds,
    emailTemplatesGridType,
    gridRefreshKey,
    selectedRowIds,
    actionLoading,
    previewOpen,
    previewTemplateId,
    handleBulkApprove,
    handleBulkReject,
    handleCellClick,
    handleMultiSelect,
    closePreview,
    resetModalState,
  };
}
